import { WorkspaceStats } from '../types/workspaces';

/**
 * Calculate total number of blocks from workspace stats
 */
export const getTotalBlocks = (stats: WorkspaceStats): number => {
  return stats.blocks ? Object.keys(stats.blocks).length : 0;
};

/**
 * Calculate total apartment units across all blocks
 */
export const getTotalApartmentUnits = (stats: WorkspaceStats): number => {
  return stats.blocks ? Object.values(stats.blocks).reduce((sum, block) => sum + block.totalApartmentUnits, 0) : 0;
};

/**
 * Calculate total parkings across all blocks
 */
export const getTotalParkings = (stats: WorkspaceStats): number => {
  return stats.blocks ? Object.values(stats.blocks).reduce((sum, block) => sum + block.totalParkings, 0) : 0;
};

/**
 * Calculate total amenities across all blocks
 */
export const getTotalAmenities = (stats: WorkspaceStats): number => {
  return stats.blocks ? Object.values(stats.blocks).reduce((sum, block) => sum + block.totalAmenities, 0) : 0;
};

/**
 * Get total residents from workspace stats (top-level)
 */
export const getTotalResidents = (stats: WorkspaceStats): number => {
  return stats.totalResidents || 0;
};

/**
 * Get all calculated totals in one object
 */
export const getAllTotals = (stats: WorkspaceStats) => {
  return {
    totalBlocks: getTotalBlocks(stats),
    totalApartmentUnits: getTotalApartmentUnits(stats),
    totalParkings: getTotalParkings(stats),
    totalAmenities: getTotalAmenities(stats),
    totalResidents: getTotalResidents(stats),
    totalAssets: stats.totalAssets,
    totalEmployees: stats.totalEmployees,
    totalVendors: stats.totalVendors,
    totalUsers: stats.totalUsers,
  };
};

/**
 * Get block-specific stats by block ID
 */
export const getBlockStats = (stats: WorkspaceStats, blockId: string) => {
  const blockStats = stats.blocks?.[blockId] || {
    totalApartmentUnits: 0,
    totalParkings: 0,
    totalAmenities: 0,
    totalFloors: 0,
  };
  
  return {
    ...blockStats,
    totalResidents: stats.totalResidents || 0,
  };
};

/**
 * Check if workspace stats are available
 */
export const hasWorkspaceStats = (stats: WorkspaceStats | null): stats is WorkspaceStats => {
  return stats !== null && typeof stats === 'object' && stats.blocks !== null && typeof stats.blocks === 'object';
}; 